import React, { useMemo, useState } from 'react'
import { AlertTriangle, RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '../ui/Button'
import { useRfidIdentifiers } from '../../hooks/useRfidIdentifiers'
import type { RfidMatrixRow } from '../../types/rfid'
import { RfidEpcPreview } from './rfid-epc-preview'
import { RfidSchemeEditor } from './rfid-scheme-editor'
import { RfidIdentifierMatrix } from './rfid-identifier-matrix'

export function RfidIdentifierTab() {
  const {
    canEdit,
    scheme,
    groups,
    isLoading,
    isSaving,
    error,
    duplicateItemCodes,
    duplicateColorCodes,
    duplicateSizeCodes,
    refetch,
    saveItemIdentifier,
    saveColorIdentifier,
    saveSizeIdentifier,
    saveScheme,
    fillMissing,
  } = useRfidIdentifiers()

  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)
  const [fillMessage, setFillMessage] = useState<string | null>(null)
  const [fillError, setFillError] = useState<string | null>(null)

  const selectedRow = useMemo(() => {
    if (!selectedProductId) return null
    for (const group of groups) {
      const match = group.variants.find((row) => row.productId === selectedProductId)
      if (match) return match
    }
    return null
  }, [groups, selectedProductId])

  const missingCount = useMemo(
    () => groups.filter((g) => g.hasMissingIdentifiers).length,
    [groups]
  )

  const invalidCodeIssues = useMemo(() => {
    const issues: string[] = []
    for (const group of groups) {
      for (const row of group.variants) {
        for (const issue of row.lengthIssues) {
          const label = `${row.itemName} · ${row.sku}: ${issue}`
          if (!issues.includes(label)) issues.push(label)
        }
      }
    }
    return issues
  }, [groups])

  function handleSelectRow(row: RfidMatrixRow) {
    setSelectedProductId(row.productId)
  }

  async function handleFillMissing() {
    setFillError(null)
    setFillMessage(null)
    try {
      const summary = await fillMissing()
      setFillMessage(
        `Filled identifiers: ${summary.itemsUpdated} items, ${summary.colorsCreated} colors, ${summary.sizesCreated} sizes, ${summary.stylesFilled} styles, ${summary.yearsFilled} years, ${summary.seasonsFilled} seasons.`
      )
    } catch (err) {
      setFillError(err instanceof Error ? err.message : 'Failed to fill identifiers')
    }
  }

  if (isLoading) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-10 text-center text-gray-500 dark:text-gray-400">
        Loading RFID identifiers…
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-white dark:bg-gray-900 p-6 space-y-3">
        <div className="flex items-start gap-2 text-red-600 dark:text-red-400">
          <AlertTriangle className="h-5 w-5 mt-0.5" />
          <div>
            <div className="font-semibold">Could not load RFID identifiers</div>
            <div className="text-sm mt-1">{error}</div>
          </div>
        </div>
        <Button variant="secondary" onClick={() => void refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    )
  }

  if (!scheme) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-gray-500 dark:text-gray-400">
        No EPC scheme available for this company.
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-5">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              RFID Item Identifier
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
              Configure item, color and size identifiers used to build the EPC base for each SKU.
              Master data only — no inventory changes and no physical unit serial allocation.
            </p>
            {!canEdit && (
              <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                View only. Ask an admin to edit RFID identifiers.
              </p>
            )}
          </div>
          {canEdit && (
            <div className="flex flex-col items-stretch sm:items-end gap-2 shrink-0">
              <Button onClick={() => void handleFillMissing()} disabled={isSaving}>
                <Sparkles className="h-4 w-4 mr-2" />
                {isSaving ? 'Filling…' : 'Fill missing identifiers'}
              </Button>
              <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs text-right">
                Fills empty fields only; pads short codes to match the EPC scheme.
              </p>
            </div>
          )}
        </div>

        {fillMessage && (
          <div className="mt-4 rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 px-4 py-3 text-sm text-teal-800 dark:text-teal-200">
            {fillMessage}
            {missingCount > 0
              ? ` ${missingCount} product group(s) still incomplete (usually missing color/size on the product).`
              : ' All product groups can compose a valid EPC base where color and size exist.'}
          </div>
        )}
        {fillError && (
          <div className="mt-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {fillError}
          </div>
        )}

        {invalidCodeIssues.length > 0 && (
          <div className="mt-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3">
            <div className="flex items-start gap-2 text-red-700 dark:text-red-300">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-semibold">
                  {invalidCodeIssues.length} identifier code
                  {invalidCodeIssues.length === 1 ? '' : 's'} cannot be encoded into an EPC
                </div>
                <ul className="mt-1 space-y-0.5 text-xs">
                  {invalidCodeIssues.slice(0, 5).map((issue) => (
                    <li key={issue} className="font-mono break-all">
                      {issue}
                    </li>
                  ))}
                </ul>
                {invalidCodeIssues.length > 5 && (
                  <div className="text-xs mt-1">
                    and {invalidCodeIssues.length - 5} more.
                  </div>
                )}
                <div className="text-xs mt-1.5">
                  An EPC is hexadecimal, so identifier codes must be digits of the exact
                  segment length. Fix them here or run Fill missing identifiers to reissue.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <RfidSchemeEditor
          scheme={scheme}
          canEdit={canEdit}
          isSaving={isSaving}
          onSave={async (input) => {
            await saveScheme(input)
          }}
        />
        <RfidEpcPreview scheme={scheme} selectedRow={selectedRow} />
      </div>

      <RfidIdentifierMatrix
        groups={groups}
        canEdit={canEdit}
        isSaving={isSaving}
        duplicateItemCodes={duplicateItemCodes}
        duplicateColorCodes={duplicateColorCodes}
        duplicateSizeCodes={duplicateSizeCodes}
        selectedProductId={selectedProductId}
        onSelectRow={handleSelectRow}
        onSaveItem={saveItemIdentifier}
        onSaveColor={saveColorIdentifier}
        onSaveSize={saveSizeIdentifier}
      />
    </div>
  )
}
